import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { userService } from '@/services/user.service';
import { useAppDispatch, useAppSelector } from '@/store';
import { setAuth } from '@/store/authSlice';
import { PaginationParams, User } from '@/types';
import { QUERY_KEYS } from '@/utils/constants';

export const useUsers = (params?: PaginationParams, enabled = true) =>
  useQuery({
    queryKey: [...QUERY_KEYS.USERS.ALL, params],
    queryFn: () => userService.getAll(params),
    // Keep the current page/results visible while the next page or a new search
    // loads, so the table never blanks out mid-interaction.
    placeholderData: keepPreviousData,
    enabled,
  });

export const useUser = (id: number) =>
  useQuery({
    queryKey: QUERY_KEYS.USERS.BY_ID(id),
    queryFn: () => userService.getById(id),
    enabled: !!id,
  });

// Distinct departments for the admin audit-log employee filter.
export const useDepartments = (enabled = true) =>
  useQuery({
    queryKey: [...QUERY_KEYS.USERS.ALL, 'departments'],
    queryFn: () => userService.getDepartments(),
    enabled,
    staleTime: 1000 * 60 * 10,
  });

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => userService.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.ALL }),
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: Parameters<typeof userService.update>[1];
    }) => userService.update(id, payload),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.BY_ID(id) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.ALL });
    },
  });
};

export const usePendingUsers = () =>
  useQuery({
    queryKey: QUERY_KEYS.USERS.PENDING,
    queryFn: userService.getPending,
    placeholderData: keepPreviousData,
  });

export const useApproveUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => userService.approve(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.PENDING });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.ALL });
    },
  });
};

export const useRejectUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => userService.reject(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.PENDING }),
  });
};

// On success, also updates the Redux auth user so ProtectedRoute's
// onboarding-gate check re-evaluates immediately, without a refetch/reload.
export const useCompleteOnboarding = () => {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  return useMutation({
    mutationFn: (payload: Parameters<typeof userService.completeOnboarding>[0]) =>
      userService.completeOnboarding(payload),
    onSuccess: (user: User) => {
      dispatch(setAuth({ accessToken: accessToken ?? '', user }));
    },
  });
};
